import * as React from "react";
import { Formik, Form, Field, FieldArray } from "formik";
import { v4 as uuidv4 } from "uuid";

import { IExercise } from "../../../resources/user/types";
import { ReactComponent as MinusIcon } from "../../../components/icons/minus.svg";
import { ReactComponent as AtIcon } from "../../../components/icons/at.svg";
import { ReactComponent as XIcon } from "../../../components/icons/x.svg";
import Button from "../../../components/button";
import SaveButton from "../../../components/save-button";
import Block from "./block";
import validationSchema from "./exercise.schema";
import Title from "../../../components/title";

import styles from "./edit.module.css";

interface IProps {
  exercise: IExercise;
  onSubmit: (exerciseId: string, newValues: Partial<IExercise>) => void;
}

function Exercise({ exercise, onSubmit }: IProps) {
  const [editMode, setEditMode] = React.useState(false);

  function getLastExercise(arr: any) {
    return arr[arr.length - 1];
  }

  function getValidationError(errors: any, index: number) {
    const exerciseSet = (errors.exerciseSets || [])[index];

    if (!exerciseSet) {
      return "";
    }
    if (exerciseSet.sets) {
      return <div>{exerciseSet.sets}</div>;
    }
    if (exerciseSet.reps) {
      return <div>{exerciseSet.reps}</div>;
    }
    if (exerciseSet.weight) {
      return <div>{exerciseSet.weight}</div>;
    }
    return "";
  }

  function isValid(
    errors: any,
    index: number,
    field: "sets" | "reps" | "weight"
  ) {
    const exerciseSet = (errors.exerciseSets || [])[index];

    if (!exerciseSet) {
      return true;
    }

    return !exerciseSet[field];
  }

  async function onSubmitExercise(values: Partial<IExercise>) {
    const newValues = {
      exerciseSets: (values.exerciseSets || []).map((exerciseSet, index) => {
        return {
          ...exerciseSet,
          pos: index
        };
      })
    };
    await onSubmit(exercise.id, newValues);
    setEditMode(false);
  }

  function switchEditSave() {
    setEditMode(true);
  }

  const onClickCancel = (resetForm: any) => () => {
    resetForm({
      exerciseSets: exercise.exerciseSets
    });
    setEditMode(false);
  };

  return (
    <Formik
      validateOnChange
      validationSchema={validationSchema}
      initialValues={{
        exerciseSets: exercise.exerciseSets
      }}
      onSubmit={onSubmitExercise}
    >
      {({ values, errors, resetForm }) => (
        <Form>
          <FieldArray name="exerciseSets">
            {arrayHelpers => (
              <div className={styles.exercise}>
                <div className={styles.head}>
                  <Title>{exercise.title}</Title>
                  {!editMode && (
                    <SaveButton onClick={switchEditSave}>Edit</SaveButton>
                  )}
                  {editMode && (
                    <div className={styles.blockCancelSave}>
                      <SaveButton
                        className={styles.cancel}
                        type="button"
                        onClick={onClickCancel(resetForm)}
                      >
                        cancel
                      </SaveButton>
                      <SaveButton className={styles.save} type="submit">
                        Save
                      </SaveButton>
                    </div>
                  )}
                </div>
                {(values.exerciseSets || []).map((exerciseSet, index) => {
                  const lengthExerciseSets =
                    (values.exerciseSets || []).length === 1;
                  const fieldError = getValidationError(errors, index);
                  return (
                    <div key={exerciseSet.id}>
                      <div className={styles.wrap}>
                        {editMode && !lengthExerciseSets && (
                          <div className={styles.icon}>
                            <MinusIcon
                              onClick={() => {
                                arrayHelpers.remove(index);
                              }}
                            />
                          </div>
                        )}
                        <Block hasError={!isValid(errors, index, "sets")}>
                          <Field
                            disabled={!editMode}
                            type="number"
                            name={`exerciseSets.${index}.sets`}
                            className={styles.input}
                          />
                          <div className={styles.caption}>SETS</div>
                        </Block>
                        <div className={styles.icon}>
                          <XIcon />
                        </div>
                        <Block hasError={!isValid(errors, index, "reps")}>
                          <Field
                            disabled={!editMode}
                            type="number"
                            className={styles.input}
                            name={`exerciseSets.${index}.reps`}
                          />
                          <div className={styles.caption}>REPS</div>
                        </Block>
                        <div className={styles.icon}>
                          <AtIcon />
                        </div>
                        <Block
                          isWide
                          hasError={!isValid(errors, index, "weight")}
                        >
                          <Field
                            disabled={!editMode}
                            type="number"
                            className={styles.input}
                            name={`exerciseSets.${index}.weight`}
                          />
                          <div className={styles.caption}>WEIGHT</div>
                        </Block>
                      </div>
                      <div className={styles.error}>{fieldError}</div>
                      <Field
                        type="number"
                        className={styles.inputHidden}
                        value={index}
                        name={`exerciseSets.${index}.pos`}
                      />
                    </div>
                  );
                })}
                {editMode && (
                  <Button
                    onClick={() => {
                      const value = getLastExercise(values.exerciseSets);
                      arrayHelpers.push({
                        ...value,
                        id: uuidv4()
                      });
                    }}
                    type="button"
                    className={styles.addButon}
                  >
                    Add Set
                  </Button>
                )}
              </div>
            )}
          </FieldArray>
        </Form>
      )}
    </Formik>
  );
}
export default Exercise;
